// Component: Sparkline.jsx
// Purpose: DS Sparkline primitive — a tiny axis-less trend line (optionally an
//   area fill) driven by a plain number[]. Generalized from the local sparkline
//   in MobileDashboard.jsx. Colors come from DS tokens: mono (currentColor),
//   profit/loss, or `auto` (sign of last−first). No axes, no dots except an
//   optional last-point dot. Pure SVG so it scales to any width/height.
//
// Usage:
//   <Sparkline data={[3,5,4,8,7,11]} tint="auto" fill />
//   <Sparkline data={series} width={104} height={48} tint="profit" animate />

import React, { useId } from "react";
import { motion } from "framer-motion";
import { cx } from "./cx";
import { SPRING_SMOOTH } from "./motion";

// tint → stroke color. `mono` inherits currentColor so callers control it.
const TINT_STROKE = {
  mono: "currentColor",
  profit: "var(--color-profit)",
  loss: "var(--color-loss)",
};

export function Sparkline({
  data = [],
  width = 104,
  height = 48,
  tint = "mono",
  strokeWidth = 1.5,
  animate = false,
  fill = false,
  lastDot = false,
  className,
  ...props
}) {
  //! Hooks run unconditionally (rules-of-hooks) — call before any early return.
  const gradId = useId();

  //! Need at least two points to draw a segment.
  if (!Array.isArray(data) || data.length < 2) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const span = max - min || 1;
  const n = data.length;

  // Inset vertically by the stroke so the top/bottom peaks aren't clipped.
  const pad = strokeWidth + 0.5;
  const usableH = height - pad * 2;
  const pts = data.map((v, i) => {
    const x = (i / (n - 1)) * width;
    // SVG y grows downward → higher value sits higher on screen.
    const y = pad + (1 - (v - min) / span) * usableH;
    return [x, y];
  });

  const line = pts
    .map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(2)} ${y.toFixed(2)}`)
    .join(" ");
  const area = `${line} L${width} ${height} L0 ${height} Z`;

  const resolved = tint === "auto" ? (data[n - 1] >= data[0] ? "profit" : "loss") : tint;
  const stroke = TINT_STROKE[resolved] || TINT_STROKE.mono;
  const [lastX, lastY] = pts[pts.length - 1];

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      className={cx("ds-sparkline", className)}
      style={{ display: "block", color: stroke }}
      aria-hidden="true"
      {...props}
    >
      {fill && (
        <>
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={stroke} stopOpacity="0.35" />
              <stop offset="100%" stopColor={stroke} stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={area} fill={`url(#${gradId})`} stroke="none" />
        </>
      )}
      {animate ? (
        <motion.path
          d={line}
          fill="none"
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={SPRING_SMOOTH}
        />
      ) : (
        <path
          d={line}
          fill="none"
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
      )}
      {lastDot && (
        <circle cx={lastX} cy={lastY} r={strokeWidth + 1} fill={stroke} vectorEffect="non-scaling-stroke" />
      )}
    </svg>
  );
}

export default Sparkline;
